import { Box } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
} from "../../../../../../../components/app/provider/patient/chart";
import Flex from "../../../../../../../components/Flex";
import SelectDateRangePicker from "../../../../../../../components/SelectDateRangePicker";
import { CreateVisitNoteIcon } from "../../../../../../../components/svg-icons/VisitNoteIcon";
import Text from "../../../../../../../components/Text";
import { useRoleController } from "../../../../../../../components/useRoleController";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import { useInfiniteScroll } from "../../../../../../../hooks/use-infinite-scroll";
import { getStartEndDateOfSameDay } from "../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
} from "../../../../../../../utils/utils";
import { PatientTimelineEvent } from "../../patient-chart/chronological-records/use-chronological-record";
import { useChronologicalRecordPaginated } from "../../patient-chart/chronological-records/use-chronological-record-paginated";
import { useRequiringActionPaginated } from "../../patient-chart/chronological-records/use-requiring-action-paginated";
import CreateVisitNoteModal from "../visit-history/CreateVisitNoteModal";
import PatientChronology from "./PatientChronology";
import PatientRequiringAction from "./PatientRequiringAction";
// import SearchInput from "../../../../../../../components/SearchInput";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";

interface PatientChronologicalAndRequiringActionLayoutProps {
  shouldHideCreateVisitNote?: boolean;
  showColumnView?: boolean;
}

const isTimelineEventArray = (v: unknown): v is PatientTimelineEvent[] =>
  Array.isArray(v);

// The v2 timeline endpoint can answer in two shapes: a Spring page
// (`{ data: { content, page } }`) or, when it groups server-side, a date-keyed
// map (`{ "2026-07-15": Event[] }`, optionally wrapped in `{ data: <map> }`).
// Normalise every shape into the flat, day-descending event array the
// infinite-scroll + regrouping pipeline expects; returns undefined when the
// record can't be interpreted (so callers keep their loading/empty state).
const extractTimelineEvents = (
  record: any
): PatientTimelineEvent[] | undefined => {
  const content = record?.data?.content;

  const groups = Object.values(content ?? {});
  if (groups.length > 0 && groups.every(isTimelineEventArray)) {
    return (groups as PatientTimelineEvent[][]).flat();
  }
};

const cardHeaderSx = {
  backgroundColor: CHART_COLORS.surfaceHeader,
  border: `1px solid ${CHART_COLORS.border}`,
  borderRadius: "4px 4px 0 0",
  px: 1.5,
  py: 0.5,
  minHeight: "28px",
};

const cardBodySx = {
  border: `1px solid ${CHART_COLORS.border}`,
  borderTop: "none",
  borderRadius: "0 0 4px 4px",
  overflowY: "auto" as const,
  overflowX: "hidden" as const,
  flex: 1,
  "&::-webkit-scrollbar": { width: "24px" },
  "&::-webkit-scrollbar-track": {
    background: CHART_COLORS.surfaceWhite,
    borderLeft: `1px solid ${CHART_COLORS.border}`,
    borderRadius: "0 0 4px 0",
  },
  "&::-webkit-scrollbar-thumb": {
    background: CHART_COLORS.border,
    borderRadius: "8px",
    border: "10px solid transparent",
    backgroundClip: "padding-box",
  },
};

const PatientChronologicalAndRequiringActionLayout = ({
  shouldHideCreateVisitNote = false,
  showColumnView,
}: PatientChronologicalAndRequiringActionLayoutProps) => {
  // Range is seeded by SelectDateRangePicker via defaultPreset="this_week"
  // on first mount — providers open the Timeline on the current week
  // rather than just today. Leaving these null lets the picker decide
  // the default; switching the prop would change the seed without
  // touching this state.
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const { patientId } = useParams();
  const createVisitNoteDisclosure = useDisclosure();
  const { hasUpdate: hasUpdateCreateVisitNote } = useRoleController("Visit");
  const currentPath = window.location.pathname;
  const isVisitNote = currentPath.includes("/visit-note");

  const { startTime } = getStartEndDateOfSameDay(startDate ?? new Date());
  const { endTime } = getStartEndDateOfSameDay(endDate ?? new Date());

  const { endTime: endDateTime } = getStartEndDateOfSameDay(
    endDate ?? new Date()
  );

  // Pagination is owned here so it can be fed to the paginated query; the
  // useInfiniteScroll hook accumulates pages and advances it on scroll.
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);

  const {
    data: paginatedRecord,
    isFetching: isChronologyFetching,
    isRefetching,
  } = useChronologicalRecordPaginated({
    xTENANTID: getXtenantId(),
    patientUuid: patientId,
    startDate: startTime!,
    endDate: endDateTime!,
    pagination,
  });

  // Tolerate both response shapes (Spring page or server-grouped date-map).
  const timelineContent = useMemo(
    () => extractTimelineEvents(paginatedRecord),
    [paginatedRecord]
  );
  // Termination signal for infinite scroll. When the endpoint answers as a
  // Spring page we trust its totalElements. When it answers as a bare grouped
  // date-map there is no grand total, so we can't cap on a count — instead keep
  // requesting the next page while the current one came back FULL (=== pageSize)
  // and stop only once a short/empty page proves we've reached the end. Reporting
  // the current page's length as the total (the old fallback) made the hook think
  // it was already at the end after page 0, so it never paged.
  const currentPageCount = timelineContent?.length ?? 0;
  const pageTotalElements = (paginatedRecord as any)?.data?.page?.totalElements;
  const timelineTotalElements =
    pageTotalElements ??
    (currentPageCount < pagination.pageSize
      ? pagination.page * pagination.pageSize + currentPageCount
      : Number.MAX_SAFE_INTEGER);

  const {
    items: timelineEvents,
    atEnd: isTimelineAtEnd,
    reset: resetTimeline,
    onScroll: onTimelineScroll,
  } = useInfiniteScroll<PatientTimelineEvent>({
    pagination,
    setPagination,
    content: timelineContent,
    totalElements: timelineTotalElements,
    isFetching: isChronologyFetching || isRefetching,
    dedupeKey: "uuid",
  });

  // Group the flat, day-descending event stream into the date-keyed map that
  // PatientChronology renders. Insertion order is preserved (string keys), so
  // groups stay in the order the API returned them.
  const chronologicalRecord = useMemo(() => {
    return timelineEvents?.reduce<Record<string, PatientTimelineEvent[]>>(
      (acc, event) => {
        const key = dayjs(event.eventTimestamp).format("YYYY-MM-DD");
        if (!acc[key]) acc[key] = [];
        acc[key].push(event);
        return acc;
      },
      {}
    );
  }, [timelineEvents]);

  // Requiring Actions — same paginated + infinite-scroll pipeline as the
  // chronological events, with its own pagination state so the two panels page
  // independently.
  const [raPagination, setRaPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);

  const {
    data: requiringActionPage,
    isFetching: isRequiringActionFetching,
    isRefetching: isRequiringActionRefetching,
  } = useRequiringActionPaginated({
    xTENANTID: getXtenantId(),
    startDate: startTime!,
    endDate: endTime!,
    patientUuid: patientId,
    pagination: raPagination,
  });

  const requiringActionContent = useMemo(
    () =>
      extractTimelineEvents(requiringActionPage) as unknown as
        any[] | undefined,
    [requiringActionPage]
  );
  // Keep paging while the last page was full; stop on the first short/empty page
  // (the grouped-map response carries no grand total). Mirrors the chronological
  // termination logic above.
  const raPageCount = requiringActionContent?.length ?? 0;
  const raPageTotal = (requiringActionPage as any)?.data?.page?.totalElements;
  const requiringActionTotalElements =
    raPageTotal ??
    (raPageCount < raPagination.pageSize
      ? raPagination.page * raPagination.pageSize + raPageCount
      : Number.MAX_SAFE_INTEGER);

  const {
    items: requiringActionItems,
    atEnd: isRequiringActionAtEnd,
    reset: resetRequiringAction,
    onScroll: onRequiringActionScroll,
  } = useInfiniteScroll<any>({
    pagination: raPagination,
    setPagination: setRaPagination,
    content: requiringActionContent,
    totalElements: requiringActionTotalElements,
    isFetching: isRequiringActionFetching || isRequiringActionRefetching,
    dedupeKey: "uuid",
  });

  // Regroup the accumulated actions back into the date-keyed map that
  // PatientRequiringAction renders (grouped by the action's own date).
  const requiringActionRecord = useMemo(() => {
    return requiringActionItems?.reduce<Record<string, any[]>>((acc, event) => {
      const key = event?.actionDate
        ? dayjs(event.actionDate).format("YYYY-MM-DD")
        : "Unknown";
      if (!acc[key]) acc[key] = [];
      acc[key].push(event);
      return acc;
    }, {});
  }, [requiringActionItems]);

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Tertiary Navbar - Search, Date Picker, Create Visit Note */}
      <Flex
        alignItems="center"
        justifyContent="flex-end"
        gap={1.5}
        sx={{
          backgroundColor: CHART_COLORS.bgSecondaryBlue,
          px: 1.5,
          height: "36px",
          minHeight: "36px",
          borderBottom: `1px solid ${CHART_COLORS.border}`,
        }}
      >
        {/* communication search */}
        {/* <SearchInput
          searchKey={""}
          onSearch={() => {}}
          placeholder="Search"
          sx={{ width: "226px" }}
        /> */}
        <SelectDateRangePicker
          startDate={startDate}
          endDate={endDate}
          defaultPreset="this_week"
          onApply={(start, end) => {
            setStartDate(start);
            setEndDate(end);
            resetTimeline();
            resetRequiringAction();
          }}
          buttonSx={{
            height: CHART_DIMENSIONS.controlHeight,
            borderRadius: "4px",
            fontSize: CHART_TYPOGRAPHY.body5.fontSize,
            bgcolor: "white",
          }}
        />
        {!shouldHideCreateVisitNote &&
          hasUpdateCreateVisitNote &&
          !isVisitNote && (
            <StyledButtonV2
              label="Create Visit Note"
              variant="contained"
              onClick={() => createVisitNoteDisclosure.open()}
              startIcon={<CreateVisitNoteIcon />}
              sx={{
                height: CHART_DIMENSIONS.controlHeight,
                fontSize: CHART_TYPOGRAPHY.body5.fontSize,
                textTransform: "none",
              }}
            />
          )}
      </Flex>

      {/* Side-by-side Cards */}
      <Flex
        gap={1.5}
        flexDirection={isVisitNote || showColumnView ? "column" : "row"}
        sx={{
          flex: 1,
          overflow: "hidden",
          p: 1.5,
        }}
      >
        {/* Requiring Actions Card */}
        <Flex
          flexDirection="column"
          sx={{ flex: "0 0 43%", minWidth: 0, overflow: "hidden" }}
        >
          <Box sx={cardHeaderSx}>
            <Text
              fontWeight={CHART_TYPOGRAPHY.weight.medium}
              sx={{
                ...CHART_TYPOGRAPHY.title6,
                color: CHART_COLORS.textDark,
              }}
            >
              Requiring Actions
            </Text>
          </Box>
          <Box
            sx={cardBodySx}
            onScroll={(e) => onRequiringActionScroll(e.currentTarget)}
          >
            <PatientRequiringAction
              data={requiringActionRecord}
              isLoadingData={
                (isRequiringActionFetching || isRequiringActionRefetching) &&
                requiringActionItems.length > 0
              }
              isEndOfList={
                isRequiringActionAtEnd && requiringActionItems.length > 0
              }
            />
          </Box>
        </Flex>

        {/* Chronological Events Card */}
        <Flex
          flexDirection="column"
          sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}
        >
          <Box sx={cardHeaderSx}>
            <Text
              fontWeight={CHART_TYPOGRAPHY.weight.medium}
              sx={{
                ...CHART_TYPOGRAPHY.title6,
                color: CHART_COLORS.textDark,
              }}
            >
              Chronological Events
            </Text>
          </Box>
          <Box
            sx={cardBodySx}
            onScroll={(e) => onTimelineScroll(e.currentTarget)}
          >
            <PatientChronology
              chronologicalRecord={chronologicalRecord}
              isLoadingData={isChronologyFetching}
              isEndOfList={isTimelineAtEnd && timelineEvents.length > 0}
            />
          </Box>
        </Flex>
      </Flex>

      {createVisitNoteDisclosure.opened && (
        <CreateVisitNoteModal disclosureHost={createVisitNoteDisclosure} />
      )}
    </Box>
  );
};

export default PatientChronologicalAndRequiringActionLayout;
